/**
 * ZenMarkup Privacy Analytics — Charts module.
 *
 * Renders metric cards, top pages table, and top sources table.
 */

import { formatCompact } from './utils.js';
import { setWidgetContent, emptyState } from './table-builder.js';
import { build as listBuild } from './list.js';

/**
 * Format seconds into a human-readable duration string.
 *
 * @param {number} seconds
 * @return {string} e.g. "2m 15s", "45s", "0s"
 */
export function formatDuration(seconds) {
	const s = Math.max(0, Math.round(seconds));

	if (s < 60) {
		return s + 's';
	}

	const m = Math.floor(s / 60);
	const remainder = s % 60;

	return remainder > 0 ? m + 'm ' + remainder + 's' : m + 'm';
}

/**
 * Format a number with compact formatting.
 *
 * @param {number} n
 * @return {string}
 */
export function formatNumber(n) {
	return formatCompact(n);
}

/**
 * Set text content of an element by data-el attribute.
 *
 * @param {string} name  The data-el value.
 * @param {string} text
 */
function setText(name, text) {
	const el = document.querySelector('[data-el="' + name + '"]');
	if (el) {
		el.textContent = text;
	}
}

/**
 * Populate the four metric cards from API data.
 *
 * @param {Object} data
 */
function renderMetrics(data) {
	setText('val-visitors', formatNumber(data.visitors || 0));
	setText('val-pageviews', formatNumber(data.pageviews || 0));
	setText('val-bounce-rate', (data.bounce_rate || 0).toFixed(1) + '%');
	setText('val-avg-duration', formatDuration(data.avg_duration || 0));
	renderBadges(data);
}

/**
 * Render trend badges on metric cards.
 *
 * @param {Object} data  Full stats response with comparison object.
 */
function renderBadges(data) {
	var block = document.querySelector('[data-el="chart-block"]');
	var iconUp  = block ? block.getAttribute('data-icon-trend-up')  : '';
	var iconDown = block ? block.getAttribute('data-icon-trend-down') : '';

	const cmp = data.comparison || {};
	var metrics = [
		{ key: 'visitors',     cur: data.visitors || 0,      prev: cmp.prev_visitors,     pct: true,  invert: false },
		{ key: 'pageviews',    cur: data.pageviews || 0,     prev: cmp.prev_pageviews,    pct: true,  invert: false },
		{ key: 'bounce_rate',  cur: data.bounce_rate || 0,   prev: cmp.prev_bounce_rate,  pct: false, invert: true },
		{ key: 'avg_duration', cur: data.avg_duration || 0,  prev: cmp.prev_avg_duration, pct: true,  invert: false }
	];

	metrics.forEach(function (m) {
		var card = document.querySelector('[data-el="metric-card"][data-metric="' + m.key + '"]');
		if (!card) return;
		var row = card.querySelector('[data-el="metric-row"]');
		if (!row) return;

		// Remove existing badge.
		var old = row.querySelector('.zmpa-badge');
		if (old) old.remove();

		if (m.prev === undefined || m.prev === null) return;

		var change;
		if (m.pct) {
			// Percent change for numeric metrics.
			change = m.prev > 0 ? ((m.cur - m.prev) / m.prev) * 100 : 0;
		} else {
			// Absolute difference for bounce rate (percentage points).
			change = m.cur - m.prev;
		}

		if (Math.abs(change) < 0.1) return;

		var isUp = change > 0;
		var isGood = m.invert ? !isUp : isUp;
		var label = m.pct
			? Math.abs(change).toFixed(0) + '%'
			: Math.abs(change).toFixed(1) + '%';

		var badge = document.createElement('span');
		badge.className = 'zmpa-badge';

		var arrowSpan = document.createElement('span');
		arrowSpan.className = isGood ? 'zmpa-badge-good' : 'zmpa-badge-bad';
		arrowSpan.innerHTML = isUp ? iconUp : iconDown;

		badge.appendChild(arrowSpan);
		badge.appendChild(document.createTextNode(' ' + label));

		var titles = {
			visitors: 'Visitors compared to previous period',
			pageviews: 'Pageviews compared to previous period',
			bounce_rate: 'Bounce rate change vs previous period (lower is better)',
			avg_duration: 'Avg. duration compared to previous period'
		};
		badge.title = titles[m.key] || '';

		row.appendChild(badge);
	});
}

/**
 * Render the top pages table.
 *
 * @param {Object} data
 */
function renderTopPages(data) {
	const pages = data.top_pages || [];

	if (!pages.length) {
		setWidgetContent('top-pages', emptyState('No page data yet.'));
		return;
	}

	const rows = pages.map(function (p) {
		return { name: p.url, count: p.count };
	});

	setWidgetContent('top-pages', listBuild('Page', 'Views', rows));
}

/**
 * Render the top sources table.
 *
 * @param {Object} data
 */
function renderTopSources(data) {
	const refs = data.top_referrers || [];

	if (!refs.length) {
		setWidgetContent('top-sources', emptyState('No referrer data yet.'));
		return;
	}

	const rows = refs.map(function (r) {
		return { name: r.referrer, count: r.count };
	});

	setWidgetContent('top-sources', listBuild('Source', 'Visits', rows));
}

/**
 * Render core chart widgets from API data.
 *
 * @param {Object} data  Response from API fetchStats().
 */
export function render(data) {
	renderMetrics(data);
	renderTopPages(data);
	renderTopSources(data);
}
